import { LogEntry } from '@monorepo/shared/componentsV2/logs/types';
import { useModalWizardNavigation } from '@monorepo/shared/componentsV2/modalWizard/modalWizardNavigationContext';
import { useToast } from '@monorepo/shared/contexts';
import { useCallback, useEffect } from 'react';
import { LogEntriesFormValues } from '../conversionHelpers';
import { Pages } from '../shared';
import { type UploadedFile } from './useUploadedFiles';

type UseLogEntriesReviewModalNavigationProps = {
  isFileSubmitted: boolean;
  clearFileSubmitted: () => void;
  areAllFilesCompletedOrHaveErrors: boolean;
  hasAnyErrorFiles: boolean;
  onClose: () => void;
  setShowErrorOverlay: (show: boolean) => void;
  selectedFile: UploadedFile | undefined;
  updateLogEntriesForFile: (
    requestId: string,
    logEntries: LogEntry[] | undefined,
  ) => void;
  handleMapLogEntryFormValuesIntoLogEntries: (
    formValues: Partial<LogEntriesFormValues>,
  ) => LogEntry[];
};

export function useLogEntriesReviewModalNavigation({
  isFileSubmitted,
  clearFileSubmitted,
  areAllFilesCompletedOrHaveErrors,
  hasAnyErrorFiles,
  onClose,
  setShowErrorOverlay,
  selectedFile,
  updateLogEntriesForFile,
  handleMapLogEntryFormValuesIntoLogEntries,
}: UseLogEntriesReviewModalNavigationProps) {
  const { showUserFriendlyErrorToast } = useToast();
  const { currentPageNumber, setPage } = useModalWizardNavigation();

  const navigateToFileUploadErrorPage = useCallback(() => {
    setPage(Pages.FILE_UPLOAD_PAGE);
    setShowErrorOverlay(true);
  }, [setPage, setShowErrorOverlay]);

  const navigateToNextFile = useCallback(() => {
    setPage(Pages.EDIT_ENTRIES_PAGE);
  }, [setPage]);

  useEffect(() => {
    if (!isFileSubmitted) return;
    clearFileSubmitted();

    if (!areAllFilesCompletedOrHaveErrors) {
      navigateToNextFile();
    } else if (hasAnyErrorFiles) {
      navigateToFileUploadErrorPage();
    } else {
      onClose();
    }
  }, [
    isFileSubmitted,
    clearFileSubmitted,
    areAllFilesCompletedOrHaveErrors,
    hasAnyErrorFiles,
    navigateToNextFile,
    navigateToFileUploadErrorPage,
    onClose,
  ]);

  const onNextPage = useCallback(
    (formValues: Partial<LogEntriesFormValues>, page: number) => {
      if (
        page === Pages.EDIT_ENTRIES_PAGE &&
        formValues &&
        selectedFile?.requestId
      ) {
        // Final form state is handled separately from the actual local logEntries state.
        // Since we can't / aren't rendering the formValues directly on the "review table",
        // we need to render LogEntryResponse[]. So:
        // 1. First we need to parse the formValues back into a LogEntryResponse.logFieldValues shape
        // 2. Then use that to set the uploaded log entries state here for visual review.

        // It would be nice if we could just directly render the form values in the review table,
        // but that requires more refactoring of how we render `useLogEntryTableColumns`, and regardless of how we
        // render the review table, we still always need to convert formValues to SaveFieldValueRequests so that we
        // can submit the data to the server.
        try {
          updateLogEntriesForFile(
            selectedFile.requestId,
            handleMapLogEntryFormValuesIntoLogEntries(formValues),
          );
        } catch (mappingError) {
          showUserFriendlyErrorToast(mappingError, undefined, {
            dontAutoHide: true,
          });
        }
      }
    },
    [
      handleMapLogEntryFormValuesIntoLogEntries,
      updateLogEntriesForFile,
      showUserFriendlyErrorToast,
      selectedFile?.requestId,
    ],
  );

  return {
    currentPageNumber,
    navigateToFileUploadErrorPage,
    navigateToNextFile,
    onNextPage,
  };
}
